<template>
    <app-layout title="Orders" class="orders">
        <page-header>
            <template #title>Orders</template>

            <template #actions v-if="isAdmin">
                <jet-dropdown v-if="showRetailPrintDropdown" align="right" width="24">
                    <template #trigger>
                        <jet-tertiary-button size="small">
                            <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                            <img class="ml-2" src="/images/icons/chevron-down.svg" width="16" height="16" alt="Print">
                        </jet-tertiary-button>
                    </template>
                    <template #content>
                        <div class="px-4 py-2 hover:bg-gray cursor-pointer" @click="handlePrint('orders')">Orders</div>
                        <div class="px-4 py-2 hover:bg-gray cursor-pointer" @click="handlePrint('labels')">Labels</div>
                    </template>
                </jet-dropdown>
                
                <jet-dropdown v-else-if="showWholesalePrintDropdown" align="right" width="24">
                    <template #trigger>
                        <jet-tertiary-button size="small">
                            <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                            <img class="ml-2" src="/images/icons/chevron-down.svg" width="16" height="16" alt="Print">
                        </jet-tertiary-button>
                    </template>
                    <template #content>
                        <div class="px-4 py-2 hover:bg-gray cursor-pointer" @click="printOrders()">Confirmations</div>
                        <div class="px-4 py-2 hover:bg-gray cursor-pointer" @click="printOrders('invoice')">Invoices</div>
                    </template>
                </jet-dropdown>

                <jet-tertiary-button v-else type="button" class="py-1" :disabled="true" >
                    <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                </jet-tertiary-button>
                
                <jet-tertiary-button type="button" onclick="window.open('/orders/utm/excel_export')" title="Order UTM Export">
                    <img src="/images/icons/excel.svg" width="14" height="14" class="mx-auto"/>&nbsp;UTM
                </jet-tertiary-button>

                <jet-tertiary-button type="button" @click="showSfdModal = true" title="SFD Order Generation">
                    <img src="/images/icons/excel.svg" width="14" height="14" class="mx-auto"/>&nbsp;SFD
                </jet-tertiary-button>

                <jet-tertiary-button v-if="form.type !== null" type="button" @click="showRoutificModal = true">
                    Routific
                </jet-tertiary-button>

                <jet-button 
                    type="button"
                    :disabled="(ordersToPrint.length === 0 && printAll === false)"
                    @click="shipAndInvoiceOrders"
                >
                    Ship &amp; Invoice
                </jet-button>

                <jet-button type="button" :href="route('orders.create')" :disabled="form.processing" >
                    Add
                </jet-button>
            </template>
        </page-header>

        <div class="px-3 md:px-4 lg:px-8">
            <div class="flex">
                <form class="flex flex-1 gap-x-2 gap-y-2 flex-wrap" @submit.prevent="handleSubmit">
                    <!-- Filters & Search for md+ screens -->
                    <div class="hidden md:flex flex-row flex-wrap gap-x-2 gap-y-2 w-full">
                        <div v-if="isAdmin" class="flex flex-col flex-1 min-w-40">
                            <jet-label for="type" value="Orders type" class="whitespace-nowrap"/>
                            <multiselect class="multi-sm h-7 text-base"
                                v-model="form.type"
                                :options="orderTypeOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :canDeselect="false"
                                :classes="{singleLabel: 'w-full px-2'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div v-if="isAdmin" class="flex flex-col flex-1 min-w-32">
                            <jet-label for="filter_by" value="Filter By" class="whitespace-nowrap"/>
                            <multiselect class="multi-sm min-w-32 h-7 text-base"
                                v-model="form.filter_by"
                                :options="filterOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                :canClear="false"
                                :canDeselect="false"
                                :classes="{singleLabel: 'w-full px-2'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div class="flex flex-col flex-1 min-w-48">
                            <jet-label for="date_range" value="Date Range" />
                            <datepicker inputClassName="dp-custom-input"
                                @update:modelValue="handleSubmit"
                                v-model="date"
                                :is24="false"
                                :enableTimePicker="false"
                                :autoApply="true"
                                range
                                twoCalendars
                                placeholder="Select Date Range"
                                :format="formatDateRange"
                                weekStart="0"
                            />
                        </div>

                        <div v-if="isAdmin" class="flex flex-col flex-1 min-w-32">
                            <jet-label value="Shipping Method" class="whitespace-nowrap"/>
                            <multiselect class="multi-sm min-w-32 h-7 text-base"
                                v-model="form.shipping_method"
                                :options="shippingOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :classes="{singleLabel: 'w-full px-2'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div v-if="isAdmin && form.type === '0' && form.shipping_method === 'delivery'" class="flex flex-col flex-1 min-w-32">
                            <jet-label for="zones" value="Zone" />
                            <div class="flex">
                                <multiselect class="multi-sm min-w-32 h-7 text-base"
                                    ref="zonesMultiselect"
                                    v-model="form.zones"
                                    :options="zoneOptions"
                                    valueProp="value"
                                    trackBy="label"
                                    label="label"
                                    mode="multiple"
                                    :multipleLabel="multipleLabel"
                                    :close-on-select="false"
                                    :hide-selected="false"
                                    placeholder="All"
                                    :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                                    @close="handleSubmit"
                                    @clear="handleSubmit"
                                >
                                    <template #beforelist>
                                        <div class="multiselect-option hover:bg-gray-100 multiselect-option font-bold" @click="selectAllZones">
                                            Select all
                                        </div>
                                        <div class="multiselect-option hover:bg-gray-100 multiselect-option font-bold" @click="deselectAllZones">
                                            Clear selection
                                        </div>
                                    </template>
                                </multiselect>
                            </div>
                        </div>

                        <div v-if="isAdmin && form.type === '0' && form.shipping_method === 'pickup'" class="flex flex-col flex-1 min-w-64">
                            <jet-label for="location" value="Location" />
                            <multiselect class="multi-sm h-7 text-base"
                                v-model="form.location"
                                :options="locationOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :classes="{ singleLabel: 'w-full px-2 whitespace-nowrap' }"
                                @change="handleSubmit"
                            />
                        </div>

                        <div v-if="form.type === '0' && form.shipping_method === 'pickup'" class="flex flex-col flex-1 min-w-64">
                            <jet-label for="pickup_timeframe" value="Pickup timeframe" />
                            <multiselect class="multi-sm h-7 text-base"
                                v-model="form.shipping_time"
                                :options="timeOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :classes="{ singleLabel: 'w-full px-2 whitespace-nowrap' }"
                                @change="handleSubmit"
                            />
                        </div>

                        <div v-if="isAdmin && isWholesaleLikeType" class="flex flex-col flex-1 min-w-40">
                            <jet-label for="cities" value="City" />
                            <multiselect class="multi-sm h-7 text-base"
                                v-model="form.cities"
                                :options="cityOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                mode="multiple"
                                :multipleLabel="multipleLabel"
                                placeholder="All"
                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div v-if="isAdmin && isWholesaleLikeType" class="flex flex-col flex-1 min-w-40">
                            <jet-label for="customer_types" value="Customer Type" />
                            <multiselect class="multi-sm h-7 text-base"
                                v-model="form.customer_types"
                                :options="customerTypes"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                mode="multiple"
                                :multipleLabel="multipleLabel"
                                placeholder="All"
                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div class="flex flex-col flex-1 min-w-36">
                            <jet-label for="status" value="Status" />
                            <multiselect class="multi-sm min-w-36 h-7 text-base"
                                v-model="form.status"
                                :options="statusOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :classes="{singleLabel: 'w-full px-2'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div v-if="isAdmin" class="flex flex-col flex-1 min-w-40">
                            <jet-label for="print" value="Printed" />
                            <multiselect class="multi-sm h-7 text-base"
                                v-model="form.print"
                                :options="printOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div class="flex flex-col flex-1 min-w-40">
                            <jet-label for="search" value="Search" />
                            <jet-input
                                type="text"
                                class="h-7 text-base"
                                name="search"
                                placeholder="Search..."
                                v-model="form.search"
                                @input="handleSubmit"
                            />
                        </div>

                        <div class="self-end">
                            <jet-tertiary-button type="button" class="py-1 h-7" @click="handleReset">
                                Reset
                            </jet-tertiary-button>
                        </div>
                    </div>

                    <!-- Filters & Search for < md screens -->
                    <div class="flex flex-col sm:flex-row flex-1 md:hidden gap-x-4 gap-y-3">
                        <div class="flex gap-x-4 gap-y-3 justify-between order-1 sm:order-2">
                            <jet-dropdown :align="dropdownPosition" width="80">
                                <template #trigger>
                                    <jet-tertiary-button type="button" class="py-1 h-7">
                                        Filters
                                        <img class="ml-2" src="/images/icons/chevron-down.svg" width="16" height="16" alt="Filters">
                                    </jet-tertiary-button>
                                </template>
                                <template #content>
                                    <div class="p-2" @click.stop>
                                        <div class="flex flex-col flex-1">
                                            <jet-label for="type" value="Orders type" class="whitespace-nowrap"/>
                                            <multiselect class="multi-sm h-7 text-base"
                                                v-model="form.type"
                                                :options="orderTypeOptions"
                                                valueProp="value"
                                                trackBy="label"
                                                label="label"
                                                placeholder="All"
                                                :canDeselect="false"
                                                :classes="{singleLabel: 'w-full px-2'}"
                                                @change="handleSubmit"
                                            />
                                        </div>

                                        <div class="flex flex-col flex-1">
                                            <jet-label for="filter_by" value="Filter By" class="whitespace-nowrap"/>
                                            <multiselect class="multi-sm h-7 text-base"
                                                v-model="form.filter_by"
                                                :options="filterOptions"
                                                valueProp="value"
                                                trackBy="label"
                                                label="label"
                                                :canClear="false"
                                                :canDeselect="false"
                                                :classes="{singleLabel: 'w-full px-2'}"
                                            />
                                        </div>

                                        <div class="flex flex-col flex-1">
                                            <jet-label for="date_range" value="Date Range" />
                                            <datepicker inputClassName="dp-custom-input"
                                                v-model="date"
                                                :is24="false"
                                                :enableTimePicker="false"
                                                :autoApply="true"
                                                range
                                                placeholder="Select Date Range"
                                                :format="formatDateRange"
                                                position="left"
                                            />
                                        </div>

                                        <div class="flex flex-col flex-1">
                                            <jet-label value="Shipping Method" class="whitespace-nowrap"/>
                                            <multiselect class="multi-sm h-7 text-base"
                                                v-model="form.shipping_method"
                                                :options="shippingOptions"
                                                valueProp="value"
                                                trackBy="label"
                                                label="label"
                                                placeholder="All"
                                                :classes="{singleLabel: 'w-full px-2'}"
                                            />
                                        </div>

                                        <div v-if="form.type === '0' && form.shipping_method === 'delivery'" class="flex flex-col flex-1">
                                            <jet-label for="zones" value="Zone" />
                                            <div class="flex">
                                                <multiselect class="multi-sm h-7 text-base"
                                                    ref="zonesMultiselect"
                                                    v-model="form.zones"
                                                    :options="zoneOptions"
                                                    valueProp="value"
                                                    trackBy="label"
                                                    label="label"
                                                    mode="multiple"
                                                    :multipleLabel="multipleLabel"
                                                    :close-on-select="false"
                                                    :hide-selected="false"
                                                    placeholder="All"
                                                    :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                                                    @close="handleSubmit"
                                                    @clear="handleSubmit"
                                                >
                                                    <template #beforelist>
                                                        <div class="multiselect-option hover:bg-gray-100 multiselect-option font-bold" @click="selectAllZones">
                                                            Select all
                                                        </div>
                                                        <div class="multiselect-option hover:bg-gray-100 multiselect-option font-bold" @click="deselectAllZones">
                                                            Clear selection
                                                        </div>
                                                    </template>
                                                </multiselect>
                                            </div>
                                        </div>

                                        <div v-if="form.type === '0' && form.shipping_method === 'pickup'" class="flex flex-col flex-1">
                                            <jet-label for="location" value="Location" />
                                            <multiselect class="multi-sm h-7 text-base"
                                                v-model="form.location"
                                                :options="locationOptions"
                                                valueProp="value"
                                                trackBy="label"
                                                label="label"
                                                placeholder="All"
                                                :classes="{ singleLabel: 'w-full px-2 whitespace-nowrap' }"
                                                @change="handleSubmit"
                                            />
                                        </div>

                                        <div class="flex flex-col flex-1">
                                            <jet-label for="status" value="Status" />
                                            <multiselect class="multi-sm h-7 text-base"
                                                v-model="form.status"
                                                :options="statusOptions"
                                                valueProp="value"
                                                trackBy="label"
                                                label="label"
                                                placeholder="All"
                                                :classes="{singleLabel: 'w-full px-2'}"
                                            />
                                        </div>

                                        <div class="flex flex-col flex-1">
                                            <jet-label for="print" value="Printed" />
                                            <multiselect class="multi-sm h-7 text-base"
                                                v-model="form.print"
                                                :options="printOptions"
                                                valueProp="value"
                                                trackBy="label"
                                                label="label"
                                                placeholder="All"
                                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                                            />
                                        </div>

                                        <div v-if="isWholesaleLikeType" class="flex flex-col flex-1">
                                            <jet-label for="cities" value="City" />
                                            <multiselect class="multi-sm h-7 text-base"
                                                v-model="form.cities"
                                                :options="cityOptions"
                                                valueProp="value"
                                                trackBy="label"
                                                label="label"
                                                mode="multiple"
                                                :multipleLabel="multipleLabel"
                                                placeholder="All"
                                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                                                @change="handleSubmit"
                                            />
                                        </div>

                                        <div v-if="isWholesaleLikeType" class="flex flex-col flex-1">
                                            <jet-label for="customer_types" value="Customer Type" />
                                            <multiselect class="multi-sm h-7 text-base"
                                                v-model="form.customer_types"
                                                :options="customerTypes"
                                                valueProp="value"
                                                trackBy="label"
                                                label="label"
                                                mode="multiple"
                                                :multipleLabel="multipleLabel"
                                                placeholder="All"
                                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                                                @change="handleSubmit"
                                            />
                                        </div>
                                    </div>
                                    <div class="flex justify-end p-2">
                                        <jet-button type="button" class="py-1 h-7" @click="handleSubmit" >
                                            Apply
                                        </jet-button>
                                    </div>

                                </template>
                            </jet-dropdown>

                            <jet-tertiary-button type="button" class="py-1 h-7" @click="handleReset" >
                                Reset
                            </jet-tertiary-button>
                        </div>
                        <div class="flex flex-1 order-2 sm:order-1">
                            <jet-input
                                type="text"
                                class="w-full h-7 text-base"
                                name="search"
                                placeholder="Search..."
                                v-model="form.search"
                                @input="handleSubmit"
                            />
                        </div>

                    </div>
                </form>
            </div>
        </div>

        <div class="px-3 md:px-4 lg:px-8 pt-3 md:pt-2 pb-4">
            <div class="rounded-t overflow-hidden border border-b-0">
                <table class="table table-with-links w-full">
                    <thead>
                        <tr>
                            <th v-if="isAdmin" class="hidden md:table-cell text-center w-0">
                                <jet-input type="checkbox" :checked="printAll === true" @change="selectAll" class="h-5 w-5"></jet-input>
                            </th>
                            <th class="text-left">BC</th>
                            <sortable-header class="text-left whitespace-nowrap" @sort="handleSort" sort-by="order_number" :direction="sort_direction" :column="sort_column">Order #</sortable-header>
                            <sortable-header v-if="!form.type && isAdmin" class="text-left whitespace-nowrap" @sort="handleSort" sort-by="is_wholesale" :direction="sort_direction" :column="sort_column">Type</sortable-header>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="name" :direction="sort_direction" :column="sort_column">Customer</sortable-header>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="order_date" :direction="sort_direction" :column="sort_column">Order Date</sortable-header>
                            <sortable-header class="text-left" @sort="handleSort" sort-by="ship_date" :direction="sort_direction" :column="sort_column">Ship Date</sortable-header>
                            <sortable-header v-if="isAdmin" class="hidden md:table-cell text-left" @sort="handleSort" sort-by="shipping_method" :direction="sort_direction" :column="sort_column">Shipping Method</sortable-header>
                            <sortable-header class="hidden md:table-cell text-left" @sort="handleSort" sort-by="status" :direction="sort_direction" :column="sort_column">Status</sortable-header>
                            <sortable-header v-if="isAdmin" class="hidden md:table-cell text-center" @sort="handleSort" sort-by="printed_at" :direction="sort_direction" :column="sort_column">Printed</sortable-header>
                            <th class="hidden md:table-cell text-right">Subtotal</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="order in orders.data" :key="order.id" :class="{ 'pickup-order' : order.shipping_method === 'pickup', 'cancelled-order': order.status === -1 }">
                            <td v-if="isAdmin" class="hidden md:table-cell text-center">
                                <input type="checkbox" v-model="ordersToPrint" :value="order.id" class="h-5 w-5 rounded" />
                            </td>
                            <td>
                                <Link class="block px-1 md:px-2 py-1" :href="route('orders.show', order.id)">
                                    <img src="/images/icons/green_check.svg" v-if="BCInfoFilled(order) == 'no_error'" width="24" height="24" class="mx-auto" />
                                    <img src="/images/icons/x-red.svg" v-if="BCInfoFilled(order) == 'error'" width="24" height="24" class="mx-auto" :title="errorMessagesBC(order)" />
                                </Link>
                            </td>
                            <td>
                                <Link class="block px-1 md:px-2 py-1" :href="route('orders.show', order.id)">
                                    {{ order.order_number }}
                                </Link>
                            </td>
                            <td v-if="!form.type && isAdmin">
                                <Link class="block px-1 md:px-2 py-1" :href="route('orders.show', order.id)">
                                    {{ order.customer?.type === 'School' ? 'Schools' : (order.is_wholesale ? 'Wholesale' : 'Retail') }}
                                </Link>
                            </td>
                            <td>
                                <Link class="block px-1 md:px-2 py-1" :href="route('orders.show', order.id)">
                                    {{ order.customer?.name || order.shipping_first_name + ' ' + order.shipping_last_name }}
                                </Link>
                            </td>
                            <td>
                                <Link class="block px-1 md:px-2 py-1" :href="route('orders.show', order.id)">
                                    <div class="hidden sm:block">
                                        {{ formatDate(order.order_date) }}
                                    </div>
                                    <div class="sm:hidden whitespace-nowrap">{{ formatDate(order.order_date, 'month-day') }}</div>
                                    <div class="sm:hidden whitespace-nowrap">{{ formatDate(order.order_date, 'year') }}</div>
                                </Link>
                            </td>
                            <td>
                                <Link class="block px-1 md:px-2 py-1" :href="route('orders.show', order.id)">
                                    <div class="hidden sm:block">{{ formatDate(order.ship_date) }}</div>
                                    <div class="sm:hidden whitespace-nowrap">{{ formatDate(order.ship_date, 'month-day') }}</div>
                                    <div class="sm:hidden whitespace-nowrap">{{ formatDate(order.ship_date, 'year') }}</div>
                                </Link>
                            </td>
                            <td v-if="isAdmin" class="hidden md:table-cell">
                                <Link class="block px-1 md:px-2 py-1 capitalize" :href="route('orders.show', order.id)">
                                    {{ order.shipping_method }}
                                </Link>
                            </td>
                            <td class="hidden md:table-cell">
                                <Link class="block px-1 md:px-2 py-1" :href="route('orders.show', order.id)">
                                    {{ order.statusDef }}
                                </Link>
                            </td>
                            <td v-if="isAdmin" class="hidden md:table-cell text-center">
                                <Link class="block px-1 md:px-2 py-1" :href="route('orders.show', order.id)">
                                    <img src="/images/icons/check.svg" v-if="order.printed_at" width="24" height="24" class="mx-auto" />
                                </Link>
                            </td>
                            <td class="hidden md:table-cell text-right">
                                <Link class="block px-1 md:px-2 py-1" :href="route('orders.show', order.id)">
                                    {{ toCurrency(subtotalBeforeDiscount(order)) }}
                                </Link>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-show="selectedCount > 0" class="mt-1 text-gray-400 text-sm">
                Selected: {{ selectedCount }} {{ selectedCount == 1 ? 'order' : 'orders'}}
            </div>

            <pagination class="inline-flex" :links="orders.links" />
        </div>

        <jet-confirmation-modal :show="showCannotPrintModal" @close="showCannotPrintModal = false">
            <template #title>
                Cannot Print
            </template>

            <template #content>
                Cannot print invoices for uninvoiced orders.
            </template>

            <template #footer>
                <jet-tertiary-button class="ml-2" @click="showCannotPrintModal = false">
                    OK
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>

        <jet-confirmation-modal :show="showCannotShipAndInvoiceModal" @close="showCannotShipAndInvoiceModal = false">
            <template #title>
                Cannot Ship &amp; Invoice
            </template>

            <template #content>
                <div v-if="selectedIncludesFutureDatesShipAndInvoice">
                    Cannot ship &amp; invoice orders with shipping dates in the future.
                </div>
                <div v-else-if="selectedIncludesInvoiced">
                    Cannot ship &amp; invoice orders that are already invoiced.
                </div>
                <div v-else>
                    Cannot Ship &amp; Invoice
                </div>
                
            </template>

            <template #footer>
                <jet-tertiary-button class="ml-2" @click="showCannotShipAndInvoiceModal = false">
                    OK
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>

        <jet-dialog-modal :show="showMarkShippedAndInvoicedModal" :closeable="!shippingAndInvoicing" @close="resetMarkShippedAndInvoicedModal()">
            <template #title>
                Ship &amp; Invoice
            </template>

            <template #content>
            
                <div v-if="shippingAndInvoicingErrorMsg">
                    {{ shippingAndInvoicingErrorMsg }}
                </div>
                <div v-else-if="shippingAndInvoicing">
                    <div class="text-center text-lg font-bold flex flex-col items-center justify-center">
                        <img src="/images/icons/loading-pulse.svg" height="128" width="128" class="" />
                        <p>Shipping &amp; Invoicing ...</p>
                    </div>
                </div>
                <div v-else-if="shippedAndInvoicedResponse === null">
                    Are you sure you want to ship &amp; invoice the {{ selectedCount }} selected order{{ selectedCount == 1 ? '' : 's' }}?
                </div>
                <div v-else>
                    <!-- Processed Orders -->
                    <div v-if="shippedAndInvoicedResponse.processed.total > 0">
                        <div>Processed Orders ({{ shippedAndInvoicedResponse.processed.total }})</div>
                        
                        <ul class="list-disc list-outside">
                            <li class="ml-8">
                                {{ shippedAndInvoicedResponse.processed.orders.join(', ') }}
                            </li>
                        </ul>
                    </div>

                    <br v-if="shippedAndInvoicedResponse.processed.total > 0 && shippedAndInvoicedResponse.unprocessed.total > 0"/>

                    <!-- Unprocessed Orders -->
                    <div v-if="shippedAndInvoicedResponse.unprocessed.total > 0">
                        Unprocessed Orders ({{ shippedAndInvoicedResponse.unprocessed.total }})

                        <ul class="list-disc list-outside">
                            <template v-for="(order_nums, response) in shippedAndInvoicedResponse.unprocessed.orders">
                                <li v-if="order_nums.length > 0" :key="response" class="ml-8">
                                    <div v-if="response == 'could_not_be_processed'">
                                        Could Not Be Processed ({{ order_nums.length }})
                                        <div class="text-xs">There was an issue processing payment. Please try again.</div>
                                    </div>
                                    <div v-else-if="response == 'invalid'">
                                        Invalid Credit Card ({{ order_nums.length }})
                                        <div class="text-xs">The customer's credit card on file is invalid. Please contact the customer and update their credit card information before shipping &amp; invoicing.</div>
                                    </div>
                                    <div v-else-if="response == 'missing'">
                                        Credit Card Missing ({{ order_nums.length }})
                                        <div class="text-xs">The customer is a credit card customer but doesn't have a credit card on file. Please contact the customer and add their credit card information before shipping &amp; invoicing.</div>
                                    </div>
                                    <div v-else>
                                        Unknown Issue ({{ order_nums.length }})
                                        <div class="text-xs">There was an unknown issue when processing payment.</div>
                                    </div>

                                    <ul class="list-disc list-outside">
                                        <li class="ml-8">
                                            {{ order_nums.join(', ') }}
                                        </li>
                                    </ul>
                                </li>
                            </template>
                        </ul>
                    </div>
                </div>
            </template>

            <template #footer>
                <div v-if="!shippingAndInvoicingErrorMsg && !shippingAndInvoicing && shippedAndInvoicedResponse === null">
                    <jet-button @click="handleShipAndInvoice">
                        Yes
                    </jet-button>

                    <jet-tertiary-button class="ml-2" @click="resetMarkShippedAndInvoicedModal()">
                        No
                    </jet-tertiary-button>
                </div>
                <div v-else>
                    <jet-tertiary-button @click="resetMarkShippedAndInvoicedModal()" :disabled="shippingAndInvoicing">
                        Close
                    </jet-tertiary-button>
                </div>
            </template>
        </jet-dialog-modal>

        <jet-dialog-modal :show="showSfdModal" @close="resetSfdModal()">
            <template #title>
                SFD Order Generation
            </template>

            <template #content>
                <div class="mb-4">
                    <jet-label for="type" value="SFD Upload Products" class="whitespace-nowrap"/>
                    <multiselect class="multi-sm h-7 text-base"
                        v-model="sfdUploadReportType"
                        :options="sfdUploadReportTypeOptions"
                        :canClear="false"
                        :canDeselect="false"
                        :classes="{singleLabel: 'w-full px-2'}"
                        :disabled="loadingSfdOrderGenerationDetails || sfdDetailsReturned"
                    />
                </div>

                <div class="mb-4">
                    <jet-label for="type" value="Ship Week" class="whitespace-nowrap"/>
                    <multiselect class="multi-sm h-7 text-base"
                        v-model="sfdShipWeek"
                        :options="sfdShipWeekOptions"
                        :canClear="false"
                        :canDeselect="false"
                        :classes="{singleLabel: 'w-full px-2'}"
                        :disabled="loadingSfdOrderGenerationDetails || sfdDetailsReturned"
                    />
                </div>

                <div class="mb-4">
                    <jet-label for="type" value="Ship Date" class="whitespace-nowrap"/>
                    <multiselect class="multi-sm h-7 text-base"
                        v-model="sfdShipDate"
                        :options="sfdShipDateOptions"
                        :canClear="false"
                        :canDeselect="false"
                        :classes="{singleLabel: 'w-full px-2'}"
                        :disabled="loadingSfdOrderGenerationDetails || sfdDetailsReturned"
                    />
                    <div class="text-xs text-gray-400">All generated orders will be shipped on the selected day of the selected week.</div>
                </div>

                <div :class="{ 'mb-4' : sfdExcelFile !== null }">
                    <div class="flex justify-around rounded-md relative w-full h-24 items-center border border-gray-dark border-dashed"
                        :class="{ 'cursor-pointer' : sfdExcelFile == null }"
                        v-on="sfdExcelFile == null ? { click: addSfdExcelFile } : {} "
                    >
                        <!-- Image exists -->
                        <div v-if="sfdExcelFile != null">
                            <div class="flex justify-around items-center">
                                <img src="/images/icons/excel.svg" width="50" height="50" alt="ExcelFile" />
                                <div class="pl-4">
                                    <div>Excel File Uploaded</div>
                                    <div class="text-xs">({{ sfdExcelFileName }})</div>
                                </div>
                            </div>
                        </div>

                        <!-- No image - upload icon/preview -->
                        <div v-show="sfdExcelFile == null">
                            <img src="/images/icons/upload.svg" width="50" height="50" alt="NoImage" />
                        </div>
                    </div>

                    <!-- Image upload -->
                    <div class="hidden">
                        <jet-input type="file" ref="sfdExcelFile"  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" @change="inputSfdExcelFile($event)" />
                    </div>

                    <jet-input-error :message="sfdExcelFileErrorMsg" class="mt-1" />
                </div>

                <div v-if="loadingSfdOrderGenerationDetails">
                    <div class="text-center text-lg font-bold flex flex-col items-center justify-center">
                        <img src="/images/icons/loading-pulse.svg" height="128" width="128" class="" />
                        <p>Loading Details ...</p>
                    </div>
                </div>
                <div v-if="sfdDetailsReturned">

                    <div v-if="this.sfdUploadReportType == 'sfd' && sfdOrderGenerationProducts.length > 0">
                        The following products will be used to generate the SFD orders:
                        <ul class="list-disc list-outside">
                            <template v-for="product, index in sfdOrderGenerationProducts">
                                <li v-if="(index + 1) <= sfdOrderGenerationProductsRequired" :key="index" class="ml-8">
                                    Column {{ sfd_product_column(index + 1) }}: {{ product.title }}
                                    <span v-if="product.description"> - {{ product.description }}</span>
                                    <span v-if="product.size"> - {{ product.size }}</span>
                                </li>
                            </template>
                        </ul>
                    </div>

                    <div v-if="sfdUploadReportType === 'sfd' && (sfdOrderGenerationProducts.length == 0 || sfdOrderGenerationProducts.length < sfdOrderGenerationProductsRequired || sfdOrderGenerationIssues.length > 0)" class="mt-4">
                        Issues with the SFD upload:
                        <ul class="list-disc list-outside">
                            <li v-if="sfdOrderGenerationProducts.length == 0" class="ml-8">
                                No SFD products set up in the system.
                            </li>
                            <li v-else-if="sfdOrderGenerationProducts.length < sfdOrderGenerationProductsRequired" class="ml-8">
                                {{ sfdOrderGenerationProductsRequired }} SFD product(s) are required and only {{ sfdOrderGenerationProducts.length }} {{ sfdOrderGenerationProducts.length == 1 ? 'is' : 'are' }} set up in the system.
                            </li>
                            <li v-for="issue, index in sfdOrderGenerationIssues" :key="index" class="ml-8" v-html="issue"></li>
                        </ul>
                    </div>
                    <div v-else-if="sfdUploadReportType === 'normal' && sfdOrderGenerationIssues.length > 0" class="mt-4">
                        Issues with the SFD upload:
                        <ul class="list-disc list-outside">
                            <li v-for="issue, index in sfdOrderGenerationIssues" :key="index" class="ml-8" v-html="issue"></li>
                        </ul>
                    </div>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-between w-full">
                    <div>
                        <jet-tertiary-button @click="resetSfdModal(false)">
                            Reset
                        </jet-tertiary-button>
                    </div>
                    <div class="flex">
                        <jet-button v-if="sfdExcelFile && !canGenerateSfdOrders && !sfdOrderGenerating" @click="retrySfdOrderGeneration">
                            Retry
                        </jet-button>

                        <jet-button v-if="sfdExcelFile && canGenerateSfdOrders" @click="handleGenerateSfdOrders" :disabled="sfdOrderGenerating">
                            <span v-if="sfdOrderGenerating">Generating</span>
                            <span v-else>Generate</span>
                            &nbsp;
                            <span v-if="sfdUploadReportType === 'normal'">{{ Object.values(sfdOrderGenerationDetails).length }}</span>
                            <span v-else>{{ sfdOrderGenerationDetails.length }}</span>
                            &nbsp;SFD Order(s)
                        </jet-button>

                        <jet-tertiary-button class="ml-2" @click="resetSfdModal()">
                            Cancel
                        </jet-tertiary-button>
                    </div>
                </div>
            </template>
        </jet-dialog-modal>

        <!-- Routific modal -->
        <jet-dialog-modal :show="showRoutificModal" @close="showRoutificModal = false" max-width="xl" id="modal">
            <template #title>
                Print Routific Deliveries
            </template>

            <template #content>
                <div v-if="printedOrders.length === 0">
                    <div class="flex items-center gap-x-1">
                        <jet-input
                            v-model="routificOrders"
                            type="text"
                            :placeholder="form.type === '0' ? 'e.g. 1201-DEL100 1201-DEL101 1201-DEL102' : 'e.g. #700000'"
                            autocomplete="off"
                            class="flex-1"
                            required
                        />
                    </div>

                    <p class="text-xs text-gray-400 mt-1">Download Routific's solution after the project has been optimized, and copy column X (Notes) into the provided text field.</p>
                </div>

                <div v-else>
                    <p class="text-red-600"><strong>Warning</strong></p>
                    <p class="mb-2">You're attempting to print <strong>{{ printedOrders.length }}</strong> orders that have already been printed.</p>
                    <p>Are you sure you want to continue?</p>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-end">
                    <template v-if="printedOrders.length === 0">
                        <jet-button v-if="form.type === '1'" :disabled="!routificOrders" @click="handleRoutificVerify">
                            Print
                        </jet-button>

                        <jet-dropdown align="left" v-else width="24" class="h-8 text-left">
                            <template #trigger>
                                <jet-button size="small">
                                    Print
                                    <font-awesome-icon :icon="['fas', 'chevron-down']" class="ml-2" />
                                </jet-button>
                            </template>

                            <template #content>
                                <div class="px-4 py-1 hover:bg-gray cursor-pointer" @click="verifyRoutificDeliveries('invoices')">Invoices</div>
                                <div class="px-4 py-1 hover:bg-gray cursor-pointer" @click="verifyRoutificDeliveries('labels')">Labels</div>
                                <div class="px-4 py-1 hover:bg-gray cursor-pointer" @click="verifyRoutificDeliveries('summary')">Summary</div>
                            </template>
                        </jet-dropdown>
                    </template>

                    <jet-button v-else @click="handleRoutificPrint">
                        Yes
                    </jet-button>

                    <jet-tertiary-button class="ml-2" @click="closeRoutificModal">
                        Cancel
                    </jet-tertiary-button>
                </div>
            </template>
        </jet-dialog-modal>
    </app-layout>
</template>

<script>
import { usePage, useForm, Link } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import { computeOrderTotals } from '@/Composables/useOrderTotals';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal.vue'
import JetDialogModal from '@/Jetstream/DialogModal.vue'
import JetDropdown from '@/Jetstream/Dropdown';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import Pagination from '@/Jetstream/Pagination';
import Multiselect from '@vueform/multiselect';
import SortableHeader from '@/Shared/SortableHeader';
import Datepicker from '@vuepic/vue-datepicker';
import { DateTime } from "luxon";

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    setup(props) {
        const { isAdmin } = UseUserAuth(usePage().props.value.user);

        let formData = {
            from: props.filters.from,
            to: props.filters.to,
            shipping_time: props.filters.shipping_time,
            status: props.filters.status,
            search: props.filters.search,
        };

        if (isAdmin.value) {
            formData.print = props.filters.print;
            formData.type = props.filters.type;
            formData.filter_by = props.filters.filter_by ? props.filters.filter_by : 'ship_date';
            formData.shipping_method = props.filters.shipping_method;
            formData.cities = props.filters.cities;
            formData.customer_types = props.filters.customer_types;
            formData.zones = props.filters.zones;
            formData.location = props.filters.location;
            formData.sort_column = props.sort_column;
            formData.sort_direction = props.sort_direction;
        }

        const form = useForm('order', formData);

        return {
            isAdmin,
            form,
        }
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetConfirmationModal,
        JetDialogModal,
        JetDropdown,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        JetTertiaryButton,
        Link,
        Multiselect,
        Pagination,
        SortableHeader,
        Datepicker,
    },

    props: ['orders', 'cityOptions', 'zoneOptions', 'locationOptions', 'customerTypes', 'filters', 'sort_column', 'sort_direction', 'other_pages_count'],

    data() {
        return {
            screenWidth: screen.availWidth,
            date: null,
            printAll: false,
            ordersToPrint: [],
            routificOrders: '',
            routificOrderIds: [],
            printedOrders: [],
            routificType: null,
            showRoutificModal: false,
            showCannotPrintModal: false,
            showCannotShipAndInvoiceModal: false,
            showMarkShippedAndInvoicedModal: false,
            shippingAndInvoicing: false,
            shippingAndInvoicingErrorMsg: null,
            shippedAndInvoicedResponse: null,
            showSfdModal: false,
            sfdUploadReportType: 'sfd',
            sfdShipWeek: null,
            sfdShipDate: 'default',
            sfdExcelFile: null,
            sfdExcelFileName: null,
            sfdExcelFileErrorMsg: null,
            sfdOrderGenerationProductsRequired: null,
            sfdOrderGenerationDetails: null,
            sfdOrderGenerationProducts: null,
            sfdOrderGenerationIssues: null,
            sfdOrderGenerating: false,

            shippingOptions: [
                { value: 'delivery', label: 'Delivery' },
                { value: 'pickup', label: 'Pickup' },
            ],

            statusOptions: [
                { value: -1, label: 'Cancelled' },
                { value: 0, label: 'New' },
                { value: 1, label: 'In Progress' },
                { value: 2, label: 'Invoiced' },
            ],

            printOptions: [
                { value: 'no_print', label: 'Not Printed' },
                { value: 'print', label: 'Printed' },
            ],

            orderTypeOptions: [
                { value: '0', label: 'Retail' },
                { value: '1', label: 'Wholesale' },
                { value: 'school', label: 'Schools' },
            ],

            timeOptions: [
                { value: 'allday', label: 'Allday' },
                { value: 'afternoon', label: 'Afternoon' },
                { value: 'early_morning', label: 'Early Morning' },
                { value: 'late_morning', label: 'Late Morning' },
                { value: 'early_afternoon', label: 'Early Afternoon' },
                { value: 'late_afternoon', label: 'Late Afternoon/Evening' },
            ],

            filterOptions: [
                { value:'order_date', label:'Order Date' },
                { value:'ship_date', label: 'Ship Date' },
            ],

            sfdUploadReportTypeOptions: [
                { value:'sfd', label: 'SFD Products'},
                { value:'normal', label: 'Normal Products'},
            ],

            sfdShipDateOptions: [
                { value:'default', label: 'Default (Customer Delivery Day)'},
                { value:'monday', label: 'Monday'},
                { value:'tuesday', label: 'Tuesday'},
                { value:'wednesday', label: 'Wednesday'},
                { value:'thursday', label: 'Thursday'},
                { value:'friday', label: 'Friday'},
                { value:'saturday', label: 'Saturday'},
            ]
        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.printAll = false;
            this.ordersToPrint = [];

            this.form
                .transform((data) => ({
                    ...data,
                    cities: this.prepareArraysForSubmit(data.cities),
                    zones: this.prepareArraysForSubmit(data.zones),
                    customer_types: this.prepareArraysForSubmit(data.customer_types),
                    from: this.date ? DateTime.fromJSDate(this.date[0]).toISODate() : null,
                    to: this.date ? DateTime.fromJSDate(this.date[1]).toISODate() : null,
                }))
                .get(route('orders.index'), { preserveState: true });
        }, 250),

        exportUtm() {
            axios
                .get(route('orders.export'))
                .then((response) => {
                    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = 'orders.xlsx';
                    link.click();
                })
                .catch((error) => {
                    console.error(error);
                });
        },

        prepareArraysForSubmit(array) {
            if (array?.length) return array;

            return !this.isAdmin ? undefined : null;
        },

        handleSort (sort) {
            this.form.sort_column = sort.column;
            this.form.sort_direction = sort.direction;
        },

        handleReset () {
            this.date = null;
            this.form.from = null;
            this.form.to = null;
            this.form.shipping_time = null;
            this.form.status = 0;
            this.form.search = '';
            this.form.sort_column = 'ship_date';
            this.form.sort_direction = 'desc';

            if (this.isAdmin) {
                this.form.print = null;
                this.form.type = null;
                this.form.filter_by = 'ship_date';
                this.form.cities = null;
                this.form.customer_types = null;
                this.form.zones = null;
                this.form.location = null;
                this.form.shipping_method = null;
            }

            this.handleSubmit();
        },

        printOrders(type = '') {
            if(this.printAll === true) {
                // Check orders on other pages
                axios
                    .get(route('orders.print.check', {
                        printType: type,
                        orders: this.ordersToPrint.join(','),
                        exclude: this.ordersToExclude.join(',')
                    }))
                    .then((response) => {
                        if(response.data.result === 'fail') {

                            if(response.data.future !== 0) this.showCannotPrintModal = true;
                            if(type == 'invoice' && response.data.uninvoiced > 0) this.showCannotPrintModal = true;

                        } else {
                            this.handlePrint(type);
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            }
            else if (type == 'invoice' && this.selectedIncludesUninvoiced) this.showCannotPrintModal = true;
            else if(this.selectedIncludesFutureDates) this.showCannotPrintModal = true;
            else this.handlePrint(type);
        },

        shipAndInvoiceOrders() {
            if(this.selectedIncludesFutureDatesShipAndInvoice) this.showCannotShipAndInvoiceModal = true;
            else if (this.selectedIncludesInvoiced) this.showCannotShipAndInvoiceModal = true;
            else if (this.selectedIncludesUninvoiced) this.showMarkShippedAndInvoicedModal = true;
        },

        resetMarkShippedAndInvoicedModal () {
            this.showMarkShippedAndInvoicedModal = false;
            this.shippingAndInvoicing = false;
            this.shippedAndInvoicedResponse = null;
            this.shippingAndInvoicingErrorMsg = null;
        },

        handleShipAndInvoice() {
            this.shippingAndInvoicing = true;

            axios.post(route('orders.mark.shipped_and_invoiced'), { 
                printAll: this.printAll,
                orders: this.ordersToPrint,
                exclude: this.ordersToExclude.join(',')
            })
            .then((response) => {
                this.reloadPage();

                this.shippedAndInvoicedResponse = response.data;

                this.shippingAndInvoicing = false;
            })
            .catch(() => {
                this.shippingAndInvoicingErrorMsg = "There was an unknown issue when shipping and invoicing.";

                this.shippingAndInvoicing = false;
            });
        },

        handlePrint(printType = '') {
            const params = {
                printAll: this.printAll,
                printType: printType,
                orders: this.ordersToPrint.join(','),
                exclude: this.ordersToExclude.join(',')
            };

            if (this.shouldAutoprint(printType)) {
                if (printType === 'labels') {
                    this.autoprintWithPrinterPrompt(route('orders.print'), params)
                        .finally(() => this.reloadPage());
                    return;
                }
                axios.get(route('orders.print', params));
            }
            else {
                window.open((route('orders.print', params)), "_blank");
            }

            this.reloadPage();
        },

        reloadPage() {
            this.printAll = false;
            this.ordersToPrint = [];

            this.$inertia.reload({ only: ['orders'] });
        },

        selectAll (e) {
            const isChecked = e.target.checked;
            this.printAll = isChecked;

            this.ordersToPrint = [];

            if (isChecked) {
                this.orders.data.forEach(o => this.ordersToPrint.push(o.id));
            }
        },

        resetSfdModal (close_modal = true) {
            if (close_modal) this.showSfdModal = false;
            this.sfdUploadReportType = 'sfd';
            this.sfdShipDate = 'default';
            this.sfdExcelFile = null;
            this.sfdExcelFileName = null;
            this.sfdExcelFileErrorMsg = null;
            this.sfdOrderGenerationProductsRequired = null;
            this.sfdOrderGenerationDetails = null;
            this.sfdOrderGenerationProducts = null;
            this.sfdOrderGenerationIssues = null;
            this.sfdOrderGenerating = false;
            // Reset ship week to first option (next week)
            if (this.sfdShipWeekOptions.length > 0) {
                this.sfdShipWeek = this.sfdShipWeekOptions[0].value;
            } else {
                this.sfdShipWeek = null;
            }
        },

        closeRoutificModal() {
            this.showRoutificModal = false;
            this.routificType = null;
            this.routificOrders = '';
            this.printedOrders = [];
            this.routificOrderIds = [];
        },

        retrySfdOrderGeneration () {
            this.resetSfdModal(false);
        },

        addSfdExcelFile () {
            this.$refs.sfdExcelFile.$el.click();
        },

        inputSfdExcelFile (event) {
            let sfdExcelFile = event.target.files[0];
            if(sfdExcelFile.type.includes('.csv')
             || sfdExcelFile.type.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
             || sfdExcelFile.type.includes('application/vnd.ms-excel'))
            {
                this.sfdExcelFileErrorMsg = null;
                this.sfdExcelFile = sfdExcelFile;
                this.sfdExcelFileName = sfdExcelFile.name;

                this.handleSfdExcelFileUpload(event);

                event.target.value = null;
            }
            else
            {
                this.sfdExcelFileErrorMsg = "The file must be a file of type: csv, xls, xlsx.";
            }
        },

        handleSfdExcelFileUpload (event) {
            let file = event.target.files[0];
            let data = new FormData();
            data.append('file', file);

            axios
                .post(route('orders.sfd.upload_' + this.sfdUploadReportType + '_products'), data)
                .then((response) => {
                    if (response.data.result === 'fail') {

                        this.sfdExcelFileErrorMsg = "There was an issue while getting details from the excel file.";

                    } else {
                        if (this.sfdUploadReportType == 'sfd') this.sfdOrderGenerationProductsRequired = response.data.products_required;
                        this.sfdOrderGenerationDetails = response.data.details;
                        if (this.sfdUploadReportType == 'sfd') this.sfdOrderGenerationProducts = response.data.products;
                        this.sfdOrderGenerationIssues = response.data.issues;
                    }
                })
                .catch((error) => {
                    console.log(error);

                    this.sfdExcelFileErrorMsg = "There was an issue while getting details from the excel file.";
                });
        },

        handleGenerateSfdOrders () {
            let data = {
                details: this.sfdOrderGenerationDetails,
                ship_date: this.sfdShipDate,
                ship_week: this.sfdShipWeek
            };

            this.sfdOrderGenerating = true;

            this.$inertia.post(this.route('orders.sfd.generate_' + this.sfdUploadReportType + '_products'), data, {
                onFinish: () => {
                    this.resetSfdModal();
                },
            });
        },

        /** 1-based product column to Excel letter: product 1 = B, 2 = C, ..., 26 = AA, 27 = AB, ... */
        sfd_product_column (sfd_product_number) {
            let k = (Number(sfd_product_number) || 0) + 1;
            let s = '';
            while (k > 0) {
                const r = (k - 1) % 26;
                s = String.fromCharCode(65 + r) + s;
                k = Math.floor((k - 1) / 26);
            }
            return s;
        },

        /** Use backend-appended values when present (Order model appends subtotal, tax, total, fee_for_delivery); otherwise use composable. */
        subtotal (order) {
            if (typeof order.subtotal === 'number') return order.subtotal;
            return computeOrderTotals(order).subtotal;
        },

        subtotalBeforeDiscount (order) {
            return computeOrderTotals(order).subtotalBeforeDiscount;
        },

        tax (order) {
            if (typeof order.tax === 'number') return order.tax;
            return computeOrderTotals(order).tax;
        },

        total (order) {
            if (typeof order.total === 'number') return order.total;
            return computeOrderTotals(order).total;
        },

        delivery_fee (order) {
            return computeOrderTotals(order).deliveryFeeAfterDiscount;
        },

        setDateRange() {
            if(this.filters.from !== null && this.filters.to !== null) {
                this.date = [];
                this.date.push(this.formatDateToJS(this.filters.from));
                this.date.push(this.formatDateToJS(this.filters.to));
            }
        },

        formatDate (date, type = 'full') {
            if(type == 'month-day') return DateTime.fromISO(date).toFormat('MMM d,');
            else if(type == 'year') return DateTime.fromISO(date).toFormat('yyyy');
            else return DateTime.fromISO(date).toFormat('MMM d, yyyy');
        },

        formatDateToJS(date) {
            return DateTime.fromISO(date).toJSDate();
        },

        formatDateRange(dateRange) {
            const start = dateRange[0];
            const end = dateRange[1];

            if(start && end)
            {
                let thisYear = DateTime.now().toFormat('yyyy');
                let startYear = DateTime.fromJSDate(start).toFormat('yyyy');
                let endYear = DateTime.fromJSDate(end).toFormat('yyyy');
                let startDate = startYear === thisYear ? DateTime.fromJSDate(start).toFormat('LLL. d') : DateTime.fromJSDate(start).toFormat("LLL. d, yyyy");
                let endDate = endYear === thisYear ? DateTime.fromJSDate(end).toFormat('LLL. d') : DateTime.fromJSDate(end).toFormat("LLL. d, yyyy");

                return startDate + ' - ' + endDate;
            }

            return null;
        },

        onWidthChange() {
            this.screenWidth = screen.availWidth;
        },

        multipleLabel(values) {
            return values.length + ' selected';
        },

        deselectAllZones() {
            this.$refs.zonesMultiselect.clear();
        },

        selectAllZones() {
            this.$refs.zonesMultiselect.clear();
            this.$refs.zonesMultiselect.select(this.getAllZonesIDs());
        },

        getAllZonesIDs() {
            return this.zoneOptions.filter(item => item.value > 0);
        },

        verifyRoutificDeliveries(type) {
            this.routificType = type;
            this.handleRoutificVerify();
        },

        handleRoutificVerify() {
            let path = this.form.type === '1' ? 'orders.routific.verify' : 'routific.print.verify';

            axios.post(route(path), { orders: this.routificOrders }).then(({data}) => {
                if (data.status === 'success') {
                    this.routificOrderIds = data.orders.map(o => o.id);
                    this.printedOrders = data.orders.filter(o => o.printed_at !== null);

                    if (!this.printedOrders.length) {
                        this.handleRoutificPrint();
                    }
                }
            })
        },

        handleRoutificPrint() {
            let path = this.form.type === '1' ? 'orders.routific.print' : `routific.print.${this.routificType}`;

            window.open(route(path, { orders: this.routificOrderIds }));

            this.closeRoutificModal();
        },

        isWholesaleOrder(id) {
            for (const order of this.orders.data) {
                if (order.id === id) {
                    return order.is_wholesale;
                }
            }
        },

        BCInfoFilled (order) {
            if (order.bc_error != null || order.order_item_bc_errors.length > 0)
                return 'error';
            else if (order.bc_id != null && order.bc_error == null && order.order_item_bc_errors.length == 0)
                return 'no_error';
            else 
                return false;
        },

        errorMessagesBC(order) {
            let message = '';

            if (order.bc_error !== null) {
                message += 'Order: ' + this.errorMessageBC(order.bc_error);

                if (order.order_item_bc_errors.length > 0) message += '\n\n';
            }

            order.order_item_bc_errors.forEach(bc_error => {
                message += 'Item: ' + this.errorMessageBC(bc_error) + '\n\n';
            });

            return message;
        },

        errorMessageBC(errorString) {
            try {
                // Check if the error string is XML
                if (errorString.startsWith('<error')) {
                    // Parse XML string
                    const parser = new DOMParser();
                    const xmlDoc = parser.parseFromString(errorString, 'application/xml');
                    const code = xmlDoc.getElementsByTagName('code')[0]?.textContent;
                    const message = xmlDoc.getElementsByTagName('message')[0]?.textContent;
                    if (code && message) {
                        return `${code}. ${message}`;
                    }
                } else {
                    // Parse JSON string
                    const errorObj = JSON.parse(errorString);
                    if (errorObj.error && errorObj.error.message) {
                        return errorObj.error.message;
                    }
                }
            } catch (e) {
                console.error('Error parsing the error string:', e);
            }

            return 'An unknown error occurred';
        },
    },

    computed: {
        today () {
            return DateTime.now();
        },

        isWholesaleLikeType() {
            return this.form.type === '1' || this.form.type === 'school';
        },

        ordersToExclude() {
            return this.orders.data.filter(i => !this.ordersToPrint.includes(i.id)).map(i => {return i.id});
        },

        isBothTypesOfOrdersSelected() {
            let type = { retail: false, wholesale: false };

            this.orders.data.forEach(order => {
                if (this.ordersToPrint.includes(order.id)) {
                    order.is_wholesale ? type.wholesale = true : type.retail = true;
                }
            })

            return type.retail && type.wholesale;
        },

        showRetailPrintDropdown() {
            if (this.ordersToPrint.length && !this.isWholesaleOrder(this.ordersToPrint[0]) && !this.isBothTypesOfOrdersSelected) {
                return true;
            }

            return false;
        },

        showWholesalePrintDropdown() {
            if (this.ordersToPrint.length && this.isWholesaleOrder(this.ordersToPrint[0]) && !this.isBothTypesOfOrdersSelected) {
                return true;
            }

            return false;
        },

        selectedCount() {
            return this.printAll === true ? this.ordersToPrint.length + this.other_pages_count : this.ordersToPrint.length;
        },

        selectedIncludesFutureDates() {
            // ! Dan requested this check be disabled Sept 21, 2022
            return false;

            // let result = false;

            // this.ordersToPrint.some(i => {
            //     let order = this.orders.data.find(j => j.id === i);
            //     let date = DateTime.fromISO(order.ship_date);
            //     if(date > this.today) {
            //         result = true;
            //         return true;    // Break on first one
            //     }
            // });
            // return result;
        },

        selectedIncludesFutureDatesShipAndInvoice() {
            let result = false;

            this.ordersToPrint.some(i => {
                let order = this.orders.data.find(j => j.id === i);
                let date = DateTime.fromISO(order.ship_date);
                if(date > this.today) {
                    result = true;
                    return true;    // Break on first one
                }
            });
            return result;
        },

        selectedIncludesUninvoiced() {
            let result = false;

            this.ordersToPrint.some(i => {
                let order = this.orders.data.find(j => j.id === i);
                if(order.status === 0 || order.status === 1) {
                    result = true;
                    return true;    // Break on first one
                }
            })
            return result;
        },

        selectedIncludesInvoiced() {
            let result = false;

            this.ordersToPrint.some(i => {
                let order = this.orders.data.find(j => j.id === i);
                if(order.status === 2) {
                    result = true;
                    return true;    // Break on first one
                }
            });
            return result;
        },

        dropdownPosition() {
            return this.screenWidth < 640 ? 'left' : 'right';
        },

        datepickerPosition() {
            return this.screenWidth < 640 ? 'right' : 'left';
        },

        sfdDetailsReturned () {
            return this.sfdExcelFile
                && (this.sfdUploadReportType !== 'sfd' || this.sfdOrderGenerationProductsRequired !== null)
                && this.sfdOrderGenerationDetails
                && (this.sfdUploadReportType !== 'sfd' || this.sfdOrderGenerationProducts)
                && this.sfdOrderGenerationIssues;
        },

        canGenerateSfdOrders () {
            return this.sfdExcelFile
                && this.sfdShipWeek
                && this.sfdShipDate
                && (this.sfdUploadReportType !== 'sfd' || (this.sfdOrderGenerationDetails && this.sfdOrderGenerationDetails.length > 0))
                && (this.sfdUploadReportType !== 'normal' || (this.sfdOrderGenerationDetails && Object.values(this.sfdOrderGenerationDetails).length > 0))
                && (this.sfdUploadReportType !== 'sfd' || (this.sfdOrderGenerationProducts && this.sfdOrderGenerationProducts.length > 0 && this.sfdOrderGenerationProducts.length >= this.sfdOrderGenerationProductsRequired))
                && this.sfdOrderGenerationIssues && this.sfdOrderGenerationIssues.length === 0
        },

        loadingSfdOrderGenerationDetails () {
            return this.sfdExcelFile && !this.sfdExcelFileErrorMsg && !this.sfdDetailsReturned;
        },

        sfdShipWeekOptions () {
            const options = [];
            const now = DateTime.now();
            const currentWeekStart = now.startOf('week'); // Monday of current week
            
            // Start from next week (exclude current week)
            let weekStart = currentWeekStart.plus({ weeks: 1 });
            
            // Generate options for the next 8 weeks
            for (let i = 0; i < 8; i++) {
                const weekEnd = weekStart.plus({ days: 6 });
                const weekValue = weekStart.toISODate();
                const weekLabel = i === 0 
                    ? `Week of ${weekStart.toFormat('MMM d')} - ${weekEnd.toFormat('MMM d, yyyy')} (Next Week)`
                    : `Week of ${weekStart.toFormat('MMM d')} - ${weekEnd.toFormat('MMM d, yyyy')}`;
                
                options.push({
                    value: weekValue,
                    label: weekLabel
                });
                
                weekStart = weekStart.plus({ weeks: 1 });
            }
            
            return options;
        },
    },

    watch: {
        "form.sort_column": {
            handler: function() {
                this.handleSubmit();
            }
        },

        "form.sort_direction": {
            handler: function() {
                this.handleSubmit();
            }
        },

        "showSfdModal": {
            handler: function(newVal) {
                if (newVal && this.sfdShipWeekOptions.length > 0) {
                    // Set default to first available week (next week)
                    this.sfdShipWeek = this.sfdShipWeekOptions[0].value;
                }
            }
        },
    },

    mounted() {
        this.setDateRange();

        window.addEventListener('resize', this.onWidthChange);

    },

    unmounted() {
        window.removeEventListener('resize', this.onWidthChange);
    },
}
</script>

<style scoped>
.pickup-order td {
    @apply bg-red-200 !important;
}

.cancelled-order td {
    @apply bg-gray-400 text-white !important;
}

.min-w-64 {
    min-width: 16rem;
}

.min-w-40 {
    min-width: 10rem;
}

.min-w-36 {
    min-width: 9rem;
}

.min-w-32 {
    min-width: 8rem;
}
</style>

<style>
.dp-custom-input {
    @apply h-7;
}
#modal {
    border-radius: 0 !important;
    overflow: visible !important;
}
</style>
